import classNames from 'classnames';
import React, { ReactNode, ForwardedRef, useState, useCallback } from 'react';

import { ErrorResponse, RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { copyToClipboard } from '@eika-infrastruktur/utils';
import { Button, ButtonTypes, IconButton, LinkButton } from '@eika/button';
import '@eika/card-style/src/ErrorCard.scss';
import { IconTypes } from '@eika/icon';
import { Modal, ModalDialogProps, ModalDialogType, SubModal } from '@eika/modal';
import RichIconTypes from '@eika/rich-icon/lib/RichIconTypes';

import Card, { CardProps } from './Card';

export interface ErrorCardProps extends Omit<CardProps, 'children'> {
    exception?: ErrorResponse | RequestExceptionWithResponse;
    description?: JSX.Element | string;
    children?: ReactNode;
    richIconType?: RichIconTypes;
    richIconMaxHeight?: number;
    errorCodeHeader?: string;
    copyErrorCode?: string;
    goBack?: string;
    informationModalProps?: {
        title: string;
        description: string;
        animateAs?: ModalDialogType;
        ref?: ForwardedRef<HTMLDivElement>;
    } & Partial<Omit<ModalDialogProps, 'title' | 'description' | 'animateAs'>>;
}

function getRcid(exception: RequestExceptionWithResponse | ErrorResponse) {
    if ('requestCorrelationId' in exception) {
        return exception.requestCorrelationId?.value;
    }
    if ('rcid' in exception) {
        return exception.rcid;
    }
    return null;
}

const ErrorCard: React.FC<ErrorCardProps> = ({
    children,
    className,
    isModal,
    title,
    description = '',
    icon,
    iconSize,
    exception,
    errorCodeHeader = 'Feilkode',
    copyErrorCode = 'Kopier feilkode',
    goBack = 'Gå tilbake',
    informationModalProps,
    ...rest
}) => {
    const [showModal, setShowModal] = useState(false);
    const exceptionRCID = exception && getRcid(exception);

    const handleIconButtonClick = () => setShowModal(true);

    const handleCopyClick = useCallback(() => exceptionRCID && copyToClipboard(exceptionRCID), [exceptionRCID]);

    const InfoModalVariant = isModal ? SubModal : Modal;

    return (
        <Card
            {...rest}
            isModal={isModal}
            icon={icon}
            iconSize={iconSize}
            title={title}
            className={classNames(className, 'sgw-error-card')}
        >
            <div className="sgw-error-card__container">
                <div className="typography-paragraph">{description}</div>
                {exceptionRCID && (
                    <div className="sgw-error-card__detailed-error-info">
                        <div
                            className={classNames('sgw-error-card__info-box', {
                                'sgw-error-card__info-box--with-icon': informationModalProps,
                            })}
                        >
                            <div className="sgw-error-card__title">
                                <span>{errorCodeHeader}</span>
                                {informationModalProps && (
                                    <IconButton
                                        icon={IconTypes.INFO_20}
                                        screenReaderText="Informasjon om feilmelding"
                                        onClick={handleIconButtonClick}
                                    />
                                )}
                            </div>
                            <span className="sgw-error-card__content">{exceptionRCID}</span>
                        </div>
                        <LinkButton icon={IconTypes.COPY_24} iconOnRightSide onClick={handleCopyClick}>
                            {copyErrorCode}
                        </LinkButton>
                    </div>
                )}
                {informationModalProps && (
                    <InfoModalVariant
                        {...informationModalProps}
                        description=""
                        setOpen={setShowModal}
                        open={showModal}
                        shouldCloseOnOverlayClick
                        className="sgw-error-card__modal"
                    >
                        <p className="sgw-error-card__modal-body-text">{informationModalProps.description}</p>
                        <div className="sgw-error-card__modal-button-container">
                            <Button buttonType={ButtonTypes.STANDARD_POSITIVE} onClick={() => setShowModal(false)}>
                                {goBack}
                            </Button>
                        </div>
                    </InfoModalVariant>
                )}
            </div>
            {children}
        </Card>
    );
};
export default ErrorCard;
